/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
/* eslint-disable react/no-danger */
import { useRef, useMemo, memo, useCallback } from "react";
import { Box } from "@mui/material";
import { ItemStatus } from "features/editor/components/Item/ItemStatus";
import { OriginalText } from "features/editor/components/Item/OriginalText";
import { Validation } from "features/editor/components/Item/Validation";
import { ApproveStatus } from "features/editor/components/Item/ApproveStatus";
import { useCursorPosition } from "features/editor/hooks/useCursorPosition";
import { useUpdateTranslation } from "features/editor/hooks/useUpdateTranslation";
import { useEditor } from "features/editor/hooks/useEditor";
import { processOriginalTextToHtml } from "features/editor/utils/utils";
import type { Language, ValidationRules } from "features/languages/types";
import type { IItemTranslation } from "features/editor/types/item";
import type { ValidationTemplate } from "features/languages/types/validation-templates";
import type { ITranslationUpdateParams } from "features/editor/types";

import {
  itemOverlayStyle,
  indexRowStyle,
  itemActiveStyle,
  itemContentStyle,
  itemNotReadyToWorkStyle,
  itemReadyToWorkStyle,
  nameRowStyle,
  nameRowTitleStyle,
  originalRowStyle,
  gapRowStyle,
  editorRowStyle,
  validationRowStyle,
  approveRowStyle,
} from "./styles";

interface ITranslationItemProps {
  isActiveItem: boolean;
  index: number;
  data: IItemTranslation;
  languageTranslation: Language;
  validationTemplate: ValidationTemplate;
  validationRules: ValidationRules;
  canApprove: boolean;
  setActiveItemIndex: any;
  setActivePanel: any;
  goTo: any;
  setListItemErrors: any;
  setListItemApprove: any;
  setListItem: any;
  syncApprovedCount: (data: ITranslationUpdateParams) => void;
}

// Comparison of the TranslationItem component.
const areEqual = (prevProps: ITranslationItemProps, nextProps: ITranslationItemProps) => {
  const equal = Object.is(prevProps.isActiveItem, nextProps.isActiveItem) && Object.is(prevProps.data, nextProps.data);

  return equal;
};

export const TranslationItem = memo(
  ({
    isActiveItem,
    data,
    languageTranslation,
    validationTemplate,
    validationRules,
    canApprove,
    setActiveItemIndex,
    setActivePanel,
    goTo,
    setListItemErrors,
    setListItemApprove,
    setListItem,
    syncApprovedCount,
  }: ITranslationItemProps) => {
    const { original, translation } = data;
    const isOriginalOk = original?.isApproved && original?.enabledToTranslate;
    const [originalMacros, originalHtml] = useMemo(() => processOriginalTextToHtml(original?.text || ""), []);
    const contentEditableRef = useRef<HTMLDivElement>(null);

    const {
      saveCursorPosition,
      restoreCursorPosition,
      removeCursorPosition,
      hasCursorPosition,
      setCursorAfterElement,
    } = useCursorPosition(contentEditableRef);

    const { editorHtml, onEditorChange, handleMacroClick } = useEditor(contentEditableRef, {
      index: data.index,
      translation,
      originalMacros,
      isOriginalOk,
      saveCursorPosition,
      restoreCursorPosition,
      hasCursorPosition,
      setCursorAfterElement,
      goTo,
    });

    const { isUpdating, onApproveHandle } = useUpdateTranslation({
      index: data.index,
      id: translation.id,
      originalId: original.id,
      translationText: translation.text,
      isApproved: data.isApproved,
      contentEditableRef,
      originalText: original?.text || "",
      originalMacros,
      saveCursorPosition,
      removeCursorPosition,
      languageTranslation,
      validationTemplate,
      validationRules,
      setListItemErrors,
      setListItemApprove,
      setListItem,
      syncApprovedCount,
    });

    // Click on item
    const handleTranslationClick = useCallback(() => {
      setActiveItemIndex(data.index);
    }, [data]);

    // Click on validation error
    const onValidationErrorClick = useCallback(
      (event: React.SyntheticEvent) => {
        event.preventDefault();
        event.stopPropagation();

        setActiveItemIndex(data.index);
        setActivePanel("Validation");
      },
      [data, isActiveItem],
    );

    const combinedStyle = useMemo(
      () => ({
        ...(isOriginalOk ? itemReadyToWorkStyle : itemNotReadyToWorkStyle),
        ...(isActiveItem ? itemActiveStyle : {}),
      }),
      [isOriginalOk, isActiveItem],
    );

    return (
      <Box sx={combinedStyle} onClick={handleTranslationClick}>
        {isUpdating && <Box sx={itemOverlayStyle} />}
        <Box sx={nameRowStyle}>
          <Box sx={indexRowStyle}>{data.index + 1}.</Box>
          <ItemStatus data={original || {}} />
          <Box sx={nameRowTitleStyle}>
            {translation.groupName} : {translation.idInFile}
          </Box>
        </Box>
        <Box sx={itemContentStyle}>
          <Box sx={originalRowStyle}>
            <OriginalText
              macros={originalMacros}
              originalHtml={originalHtml}
              handleOriginalMacroClick={handleMacroClick}
            />
          </Box>

          <Box sx={gapRowStyle} />

          <Box sx={editorRowStyle}>
            <span
              ref={contentEditableRef}
              contentEditable={Boolean(isOriginalOk && !isUpdating)}
              dangerouslySetInnerHTML={{
                __html: editorHtml,
              }}
              className={`editor__content ${isActiveItem ? "editor__content--active" : ""}`}
              tabIndex={0}
              onInput={onEditorChange}
            />
          </Box>

          <Box sx={gapRowStyle} />

          <Box sx={validationRowStyle}>
            <Validation validationErrors={data.errors} onValidationErrorClick={onValidationErrorClick} />
          </Box>

          <Box sx={approveRowStyle}>
            <ApproveStatus
              isApproved={data.isApproved}
              canApprove={canApprove && data.errors.length === 0 && isOriginalOk}
              onApproveClick={onApproveHandle}
            />
          </Box>
        </Box>
      </Box>
    );
  },
  areEqual,
);
